import React, { useContext, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ThemeToggle } from "./ThemeToggle";
import { Context } from "../context";
import { Language } from "../store/UIStore";

interface UserDropdownProps {
  isOpen: boolean;
  logout: () => void;
  onClose: () => void;
}

const languages: { code: Language; name: string; flag: string }[] = [
  { code: "pl", name: "Polski", flag: "🇵🇱" },
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "de", name: "Deutsch", flag: "🇩🇪" },
  { code: "uk", name: "Українська", flag: "🇺🇦" },
  { code: "ko", name: "한국인", flag: "🇰🇷" },
];

export const UserDropdown: React.FC<UserDropdownProps> = observer(
  ({ isOpen, logout, onClose }) => {
    const { t } = useTranslation("common");
    const { ui, user } = useContext(Context);

    // Lock page body scroll on mobile when menu is open
    useEffect(() => {
      if (isOpen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "unset";
      }
      return () => {
        document.body.style.overflow = "unset";
      };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
      <>
        {/* EN: High z-index backdrop to cover all floating mobile navigation buttons */}
        {/* PL: Tło o wysokim z-index, aby zakryć wszystkie pływające przyciski na dole */}
        <div
          className="fixed inset-0 z-[998] bg-black/40 backdrop-blur-[2px] transition-opacity"
          onClick={onClose}
        />

        {/* EN: Dropdown menu with restricted height (max-h-[65vh]) so it won't touch screen bottom */}
        {/* PL: Dropdown z ograniczoną wysokością, aby nie sięgał dolnej krawędzi ekranu */}
        <div className="absolute right-0 top-full mt-2 w-72 max-h-[65vh] sm:max-h-[80vh] overflow-y-auto origin-top-right rounded-2xl bg-stone-900 border border-amber-500/40 text-stone-100 shadow-2xl ring-1 ring-black/50 focus:outline-none z-[999] backdrop-blur-xl">
          {/* User Info Header Section */}
          {user.user && (
            <div className="px-4 py-3.5 bg-stone-950/90 border-b border-stone-800/80 sticky top-0 z-10 backdrop-blur-md">
              <p className="text-sm font-serif font-semibold text-amber-400 truncate">
                {t("userMenu.greeting", { name: user.user.firstName })}
              </p>
              <p className="text-xs text-stone-400 truncate mt-0.5">
                {user.user.email}
              </p>
            </div>
          )}

          <div className="py-2">
            {/* Appearance / Theme Control */}
            <div className="px-4 py-2.5">
              <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider">
                {t("theme.appearance")}
              </p>
              <div className="mt-2">
                <ThemeToggle />
              </div>
            </div>

            <div className="border-t border-stone-800/80 my-1"></div>

            {/* Language Selection Grid */}
            <div className="px-4 py-2.5">
              <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider">
                {t("userMenu.language")}
              </p>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => ui.setLanguage(lang.code)}
                    className={`flex items-center justify-center p-2 text-xs rounded-xl transition-all duration-200 border ${
                      ui.language === lang.code
                        ? "bg-amber-600 text-amber-50 font-semibold border-amber-400/50 shadow-md shadow-amber-950/40"
                        : "bg-stone-800/70 border-stone-700/50 text-stone-300 hover:bg-stone-800 hover:border-stone-600"
                    }`}
                  >
                    <span className="mr-1.5 text-sm">{lang.flag}</span>
                    {lang.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-stone-800/80 my-1"></div>

            {/* Profile Navigation Link */}
            <Link
              to="/profile"
              onClick={onClose}
              className="flex items-center px-4 py-2.5 text-sm text-stone-200 hover:text-amber-400 hover:bg-stone-800/60 transition"
              role="menuitem"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-2.5 text-amber-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
                />
              </svg>
              {t("userMenu.profile")}
            </Link>

            <div className="border-t border-stone-800/80 my-1"></div>

            {/* Logout Action Button */}
            <button
              onClick={logout}
              className="w-full text-left text-rose-400 hover:text-rose-300 hover:bg-rose-950/30 block px-4 py-2.5 text-sm transition font-medium"
              role="menuitem"
            >
              <div className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-2.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
                {t("userMenu.logout")}
              </div>
            </button>
          </div>
        </div>
      </>
    );
  }
);
